import type { Dayjs } from 'dayjs';

export type Selectable<T> = T | { value: T; label?: unknown };

export type DateLike = Dayjs | string | null | undefined;

export interface OrderNewItemFormValue {
  product_id?: Selectable<string | null> | null;
  sku?: string | null;
  inventory_type_id?: Selectable<string | null> | null;
  inventory_id?: Selectable<string | null> | null;
  quantity?: number | null;
  manufacturing_date?: DateLike;
  expiration_date?: DateLike;
  batch?: string | null;
  note?: string | null;
  price?: number | null;
  identifier?: string | null;
  original_price?: number | null;
  cost?: number | null;
  custom_attributes?: Record<string, unknown> | null;
  bundle_id?: string | null;
  [extra: string]: unknown;
}

export interface OrderNewFormValues {
  name?: string;
  extra_name?: string | null;
  sales_channel_id?: string | null;
  shipping_type_id?: string | null;
  country_code?: string | null;
  postal_code?: string | null;
  state?: string | null;
  city?: string | null;
  district?: string | null;
  address?: string;
  receiver_name?: string;
  phone?: string;
  email?: string | null;
  convenience_store_code?: string | null;
  total_amount?: number | string | null;
  currency?: string | null;
  is_cod?: boolean;
  delivery_with_pickup?: boolean;
  urgent?: boolean;
  scheduled_shipping_date?: DateLike;
  delivery_date?: DateLike;
  delivery_time?: string | null;
  seller_name?: string | null;
  seller_phone?: string | null;
  invoice_number?: string | null;
  note?: string | null;
  warehouse_id?: string | null;
  merchant_id?: string | null;
  order_items?: OrderNewItemFormValue[];
  [extra: string]: unknown;
}

export interface BuildOrderPayloadOptions {
  role: 'admin' | 'operator';
  /** operator 端必填，從 OperatorDataContext 取得的 merchant id */
  operatorMerchantId?: string | null;
  isDraft?: boolean;
}

export interface OrderItemPayload {
  product_id: string | null | undefined;
  sku: string | null | undefined;
  inventory_type_id: string | null | undefined;
  quantity: number | null | undefined;
  manufacturing_date: string | null;
  expiration_date: string | null;
  batch: string | null | undefined;
  note: string | null | undefined;
  price: number | null | undefined;
  identifier: string | null | undefined;
  original_price: number | null | undefined;
  cost: number | null | undefined;
  custom_attributes: Record<string, unknown> | null | undefined;
  inventory_id: string | null;
  bundle_id: string | null;
}

export interface OrderCreatePayload {
  order: {
    name: string | undefined;
    extra_name: string | null;
    sales_channel_id: string | null | undefined;
    shipping_type_id: string | null | undefined;
    country_code: string | null;
    postal_code: string | null;
    state: string | null;
    city: string | null;
    district: string | null;
    address: string | undefined;
    receiver_name: string | undefined;
    phone: string | undefined;
    email: string | null;
    convenience_store_code: string | null;
    total_amount: number | string | null;
    currency: string | null;
    is_cod: boolean | undefined;
    delivery_with_pickup: boolean | undefined;
    urgent: boolean | undefined;
    scheduled_shipping_date: string | null;
    delivery_date: string | null;
    delivery_time: string | null;
    seller_name: string | null;
    seller_phone: string | null;
    invoice_number: string | null;
    note: string | null;
    order_items: OrderItemPayload[];
    merchant_id?: string;
    warehouse_id?: string;
  };
  initial_status?: 'draft';
}

export function unwrapSelect<T>(value: Selectable<T> | null | undefined): T | null | undefined {
  if (value && typeof value === 'object' && 'value' in value) {
    return (value as { value: T }).value;
  }
  return value as T | null | undefined;
}

export function formatDate(value: DateLike): string | null {
  if (!value) return null;
  if (typeof value === 'object' && value !== null && typeof (value as Dayjs).format === 'function') {
    return (value as Dayjs).format('YYYY-MM-DD');
  }
  return value as string;
}

// custom_attributes 從 form state 直接以 Hash 形態傳到後端（orders_controller
// permit `{ custom_attributes: {} }` 接得到 nested Hash）。空 Hash / undefined 一律送 null，
// 讓 BE 落 nil 而不是空物件，避免無意義 jsonb 查詢條件。
function normalizeCustomAttributes(
  value: OrderNewItemFormValue['custom_attributes'],
): Record<string, unknown> | null {
  if (value && typeof value === 'object') return value;
  return null;
}

function mapOrderItem(item: OrderNewItemFormValue): OrderItemPayload {
  return {
    product_id: unwrapSelect(item.product_id),
    sku: item.sku,
    inventory_type_id: unwrapSelect(item.inventory_type_id),
    quantity: item.quantity,
    manufacturing_date: formatDate(item.manufacturing_date),
    expiration_date: formatDate(item.expiration_date),
    batch: item.batch,
    note: item.note,
    price: item.price,
    identifier: item.identifier,
    original_price: item.original_price,
    cost: item.cost,
    custom_attributes: normalizeCustomAttributes(item.custom_attributes),
    inventory_id: unwrapSelect(item.inventory_id) || null,
    bundle_id: item.bundle_id || null,
  };
}

/**
 * Build the request body sent to `POST /orders` from OrderNew form values.
 *
 * Pure function — no React / context access. Closure deps (`role`,
 * `operatorMerchantId`) are passed explicitly so this can be unit-tested
 * without mounting the component.
 */
export function buildOrderPayload(
  values: OrderNewFormValues,
  options: BuildOrderPayloadOptions,
): OrderCreatePayload {
  const { role, operatorMerchantId, isDraft = false } = options;

  const mergedOrderItems = (values.order_items || []).map(mapOrderItem);

  const payload: OrderCreatePayload = {
    order: {
      name: values.name,
      extra_name: values.extra_name || null,
      sales_channel_id: values.sales_channel_id,
      shipping_type_id: values.shipping_type_id,
      country_code: values.country_code || null,
      postal_code: values.postal_code || null,
      state: values.state || null,
      city: values.city || null,
      district: values.district || null,
      address: values.address,
      receiver_name: values.receiver_name,
      phone: values.phone,
      email: values.email || null,
      convenience_store_code: values.convenience_store_code || null,
      // 空 / null / undefined 都 fallback 到 0；其他值（含 "0", "0.00", 數字）原樣送 BE。
      total_amount: values.total_amount == null || values.total_amount === '' ? 0 : values.total_amount,
      currency: values.currency || null,
      is_cod: values.is_cod,
      delivery_with_pickup: values.delivery_with_pickup,
      urgent: values.urgent,
      scheduled_shipping_date: formatDate(values.scheduled_shipping_date),
      delivery_date: formatDate(values.delivery_date),
      delivery_time: values.delivery_time || null,
      seller_name: values.seller_name || null,
      seller_phone: values.seller_phone || null,
      invoice_number: values.invoice_number || null,
      note: values.note || null,
      order_items: mergedOrderItems,
    },
  };

  if (isDraft) {
    payload.initial_status = 'draft';
  }

  if (role === 'operator' && operatorMerchantId) {
    payload.order.merchant_id = operatorMerchantId;
  }

  if (role === 'admin' && values.warehouse_id) {
    payload.order.warehouse_id = values.warehouse_id;
  }

  return payload;
}
